import { api_fetch, HttpError, HttpResponse } from "@/api/client"
import { Result } from "@/utils"

export type MailboxProvider = "microsoft" | "gmail"

export type MailboxInviteStatus = "pending" | "connected" | "expired" | "revoked"

/**
 * How the person opening a connect link intends to proceed. `direct` and `delegated` only pre-fill
 * the account picker; `app_only` skips sign-in altogether and asks the tenant's admin to grant the
 * MarginWiz app access (Microsoft only).
 */
export type ConnectMode = "direct" | "delegated" | "app_only"

/** A connect link as the brand sees it in settings. */
export interface MailboxInvite {
  id: string
  created_at: string
  integration_name: MailboxProvider
  email: string
  status: MailboxInviteStatus
  expires_at: string
  completed_at: string | null
  connected_by_account: string | null
  last_attempt_at: string | null
  last_error: string | null
  /** Set once a Microsoft admin consented through the link (app-only connect). */
  tenant_id: string | null
  url: string
}

/** What the (unauthenticated) holder of a connect link is allowed to see. */
export interface PublicMailboxInvite {
  id: string
  organization_name: string
  integration_name: MailboxProvider
  email: string
  status: MailboxInviteStatus
  expires_at: string
  can_connect: boolean
  connected_by_account: string | null
  last_error: string | null
  /** The tenant whose admin consented to app-only access, once one has. */
  tenant_id: string | null
  /** Consent granted, mailbox not yet confirmed in scope: show the Exchange step and re-check. */
  awaiting_mailbox_scope: boolean
  /** Our application (client) id, which the admin's PowerShell needs. Microsoft links only. */
  microsoft_app_id: string | null
}

export interface SelfConnectRequest {
  integration_name: MailboxProvider
  email: string
  mode: ConnectMode
  /** Where to land afterwards. Must be a path on this site. */
  next?: string
}

/**
 * Connect a mailbox you can sign into yourself. Verified exactly like a connect link, but it
 * returns the provider's sign-in URL rather than minting a link anyone could open — only MarginWiz
 * staff can do that.
 */
export async function selfConnectMailbox(
  body: SelfConnectRequest
): Promise<Result<HttpResponse<string>, HttpError>> {
  return api_fetch<string>("/mailbox-invites/self-connect", { method: "POST", body })
}

export interface CreateMailboxInviteRequest {
  integration_name: MailboxProvider
  email: string
  expires_in_days?: number
}

export async function createMailboxInvite(
  body: CreateMailboxInviteRequest
): Promise<Result<HttpResponse<MailboxInvite>, HttpError>> {
  return api_fetch<MailboxInvite>("/mailbox-invites", { method: "POST", body })
}

export async function getMailboxInvites(): Promise<Result<HttpResponse<MailboxInvite[]>, HttpError>> {
  return api_fetch<MailboxInvite[]>("/mailbox-invites")
}

export async function revokeMailboxInvite(
  id: string
): Promise<Result<HttpResponse<MailboxInvite>, HttpError>> {
  return api_fetch<MailboxInvite>(`/mailbox-invites/${id}`, { method: "PATCH", body: { revoked: true } })
}

/** Public — no session required. */
export async function getPublicMailboxInvite(
  id: string
): Promise<Result<HttpResponse<PublicMailboxInvite>, HttpError>> {
  return api_fetch<PublicMailboxInvite>(`/mailbox-invites/${id}`)
}

/** Public — returns the provider sign-in URL to send the browser to. */
export async function startMailboxInvite(
  id: string,
  mode: ConnectMode,
  /** Only for in-app connects: where to land afterwards. Must be a path on this site. */
  next?: string
): Promise<Result<HttpResponse<string>, HttpError>> {
  return api_fetch<string>(`/mailbox-invites/${id}/start`, { method: "POST", body: { mode, next } })
}

/** Public — re-probes the mailbox for an app-only link whose admin has already consented. */
export async function checkMailboxInvite(
  id: string
): Promise<Result<HttpResponse<PublicMailboxInvite>, HttpError>> {
  return api_fetch<PublicMailboxInvite>(`/mailbox-invites/${id}/check`, { method: "POST", body: {} })
}

export function provider_display_name(provider: MailboxProvider): string {
  return provider === "microsoft" ? "Microsoft 365" : "Google Workspace"
}
